import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
  RouterOutlet,
} from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import {
  ToggleComponent,
  ToggleSize,
} from "@claas/claas-ui-components/form/toggle";
import { TranslatePipe } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { EmptyFavoritesList } from "../../../shared/components/empty-favorites-list/empty-favorites-list";
import { MainModalDialog } from "../../../shared/components/main-modal-dialog/main-modal-dialog";
import {
  pathParamsHelper,
  pathRouteDataHelper$,
} from "../../../shared/helpers/path-params.helper";
import { DefaultArgument } from "../../../shared/pia-api/generated";
import { FavoriteArgumentsService } from "../../../shared/services/favorite-arguments.service";
import { ModalCommunicationService } from "../../../shared/services/modal-communication-service";
import { ProductArgumentRow } from "../product-argument-row/product-argument-row";

@Component({
  selector: "claas-product-arguments-table",
  templateUrl: "./product-arguments-table.html",
  styleUrl: "./product-arguments-table.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IconComponent,
    TranslatePipe,
    ToggleComponent,
    ProductArgumentRow,
    EmptyFavoritesList,
    RouterOutlet,
    MainModalDialog,
  ],
})
export class ProductArgumentsTable implements OnInit, OnDestroy {
  private favoriteArgumentsService = inject(FavoriteArgumentsService);
  route = inject(ActivatedRoute);
  data = toSignal(pathRouteDataHelper$());
  modalCommunicationService = inject(ModalCommunicationService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  countryID = pathParamsHelper(this.route.snapshot).countryID;
  languageID = pathParamsHelper(this.route.snapshot).languageID;
  year = pathParamsHelper(this.route.snapshot).year;
  productID = pathParamsHelper(this.route.snapshot).productID;
  productDetailID = pathParamsHelper(this.route.snapshot).productDetailID;

  private subscription = new Subscription();

  showMainModal = false;

  showFavoriteArgumentsStatus = toSignal<boolean>(
    this.favoriteArgumentsService.productArgumentsFavoriteStatus$,
  );
  favoriteProductArguments = toSignal(
    this.favoriteArgumentsService.includedDocumentIds$,
  );

  productArgumentsGroupedByCategory = computed(() => {
    const productArguments = this.data()?.["arguments"] as DefaultArgument[];
    if (!productArguments || productArguments.length === 0) {
      return [];
    }

    const groupedMap = productArguments
      .filter((productArgument) =>
        !this.showFavoriteArgumentsStatus()
          ? true
          : productArgument?.ID &&
            this.favoriteProductArguments()?.includes(productArgument.ID),
      )
      .reduce(
        (acc, item) => {
          const category = item.Category as string;
          if (!acc[category]) {
            acc[category] = [];
          }
          item.Rating = item.Rating ? Math.round(item.Rating) : 0;
          acc[category].push(item);
          return acc;
        },
        {} as Record<string, DefaultArgument[]>,
      );

    return Object.entries(groupedMap).map(([category, items]) => ({
      title: category,
      items,
    }));
  });
  protected readonly toggleSize = ToggleSize;

  handleToggleFavouriteArgumentsChange(checked: boolean) {
    this.favoriteArgumentsService.setFavoritesArgumentsStatus(checked);
  }

  setShowMainModal(val: boolean) {
    this.showMainModal = val;
  }

  ngOnInit(): void {
    this.subscription = this.modalCommunicationService.closeModal$.subscribe(
      () => this.setShowMainModal(false),
    );
  }

  private hasModalRouteData(route: ActivatedRouteSnapshot): boolean {
    // Check current route data
    if (route.data["isModal"]) {
      return true;
    }

    // Check children recursively
    return route.children.some((child) => this.hasModalRouteData(child));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
